<script setup>
/**
 * @todo When the legacy theme is removed: delete the legacyProps prop, the useTheme import,
 * and collapse effectiveProps to { name: props.name, ...attrs }.
 */

import { computed, useAttrs } from 'vue';

import { useTheme } from '../composables/useTheme';

/**
 * Material Symbols icon rendered at one of six semantic size tokens.
 *
 * @status recommended
 * @group media
 * @useWhen Rendering a Material Symbols icon by name inside any design-system component or page
 * @see https://quasar.dev/vue-components/icon Quasar q-icon API
 */
defineOptions({ name: 'HrIcon' });

/**
 * @typedef {'xxs' | 'xs' | 'sm' | 'md' | 'lg' | 'xl'} HrIconSize
 */
const props = defineProps({
  /**
   * Material Symbols icon name.
   * @example thumb_up
   */
  name: {
    type: String,
    required: true,
  },
  /**
   * Icon size.
   * @values xxs, xs, sm, md, lg, xl
   */
  size: {
    type: String,
    default: 'md',
    validator: (v) => ['xxs', 'xs', 'sm', 'md', 'lg', 'xl'].includes(v),
  },
  /**
   * Legacy props are any additional Quasar props that can be passed to the icon for backwards
   * compatibility with the legacy theme. These props will be ignored when a design system theme
   * is active.
   * @type {Object} legacyProps
   * @deprecated This prop is only for backwards compatibility with the legacy theme and should be
   * removed once the legacy theme is removed.
   */
  legacyProps: {
    type: Object,
    default: () => ({}),
  },
});

const attrs = useAttrs();
const { hasTheme } = useTheme();

const effectiveProps = computed(() => {
  const base = { name: props.name };

  if (hasTheme.value) {
    return { ...base, ...attrs };
  }
  return { ...base, ...props.legacyProps, ...attrs };
});
</script>
<template>
  <q-icon
    v-bind="effectiveProps"
    :class="hasTheme ? ['hr-icon', `hr-icon--size-${props.size}`] : undefined"
  >
    <!-- @slot default Tooltip content or supplementary element overlaid on the icon. -->
    <slot />
  </q-icon>
</template>
<style lang="scss" scoped>

.hr-icon--size-xxs { font-size: var(--app-comp-icon-size-xxs); }
.hr-icon--size-xs  { font-size: var(--app-comp-icon-size-xs); }
.hr-icon--size-sm  { font-size: var(--app-comp-icon-size-sm); }
.hr-icon--size-md  { font-size: var(--app-comp-icon-size-md); }
.hr-icon--size-lg  { font-size: var(--app-comp-icon-size-lg); }
.hr-icon--size-xl  { font-size: var(--app-comp-icon-size-xl); }
</style>
